import { LoadingButton as Button } from '@mui/lab'
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { IoLogOutOutline, IoMailOutline, IoSettingsOutline } from 'react-icons/io5'
import { useDispatch } from 'react-redux'

import { useSendPasswordLinkMutation } from '@/services/api'
import { logout } from '@/store/accountSlice'
import { LoginUser, User } from '@/types'

type PasswordChangeDialogProps = {
  onCancel?: () => void
  onConfirm?: () => void | Promise<void>
  open: boolean
  loading?: boolean
  user?: User | LoginUser
  width?: DialogProps['maxWidth']
}

function PasswordChangeDialog(props: PasswordChangeDialogProps) {
  const { loading, width = 'sm', open, user } = props
  const dispatch = useDispatch()
  const [sendLink, { isError, isSuccess, data, isLoading }] =
    useSendPasswordLinkMutation()

  const handleConfirm = async () => {
    if (!user?.email) return

    await sendLink({ email: user?.email })
  }

  useEffect(() => {
    if (data && isSuccess) toast.success(data.message)
    if (isError) toast.error('Algo deu errado. Espere um pouco e tente novamente.')
  }, [isSuccess, isError, data])

  const handleLogout = async () => {
    dispatch(logout())
  }

  return (
    <Dialog
      maxWidth={width}
      fullWidth
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[24],
        },
      }}>
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontSize: '1.25rem',
          fontWeight: 600,
        }}>
        <IoSettingsOutline color="warning" />
        Atualização de Segurança Necessária
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '0.95rem',
            },
          }}>
          Por motivos de segurança, solicitamos que todos os professores atualizem sua
          senha padrão para uma senha personalizada.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, fontWeight: 500 }}>
            O link para redefinição será enviado para:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }}>
            <IoMailOutline color="action" fontSize="small" />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}>
              {user?.email}
            </Typography>
            {user?.email_verified_at && (
              <Chip
                label="Verificado"
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 'auto', fontSize: '0.75rem' }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>
        <Button
          variant="contained"
          onClick={handleConfirm}
          fullWidth
          size="large"
          loading={loading || isLoading}
          startIcon={<IoSettingsOutline />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}>
          Solicitar Link de Redefinição
        </Button>

        <Button
          variant="outlined"
          onClick={handleLogout}
          fullWidth
          size="large"
          loading={loading}
          startIcon={<IoLogOutOutline />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            },
          }}>
          Sair da Conta
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PasswordChangeDialog
